// src/components/Recruiter/JobPostings.jsx
import React, { useState } from 'react';
import { updateDoc, doc, deleteDoc } from 'firebase/firestore';
import { db } from '../../services/firebase';
import { deductTokens, getRecruiterTokens } from '../../services/recruiter';
import JobFormModal from '../Admin/JobFormModal';

const JobPostings = ({ jobs, onRefresh, recruiterId }) => {
    const [sponsoring, setSponsoring] = useState({});
    const [editingJob, setEditingJob] = useState(null);
    const [showEditModal, setShowEditModal] = useState(false);
    const [deleting, setDeleting] = useState({});

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-ZA');
    };

    const getStatusColor = (job) => {
        const closingDate = new Date(job.closingDate);
        const today = new Date();
        if (closingDate < today) return 'text-red-600 bg-red-50';
        return 'text-green-600 bg-green-50';
    };

    const handleSponsor = async (job) => {
        if (job.isSponsored) {
            alert('This job is already sponsored!');
            return;
        }

        if (new Date(job.closingDate) < new Date()) {
            alert('Cannot sponsor a closed job.');
            return;
        }

        const tokens = await getRecruiterTokens(recruiterId);
        if (tokens < 1) {
            alert('Insufficient tokens. You need 1 token to sponsor a job.');
            return;
        }

        if (!window.confirm(
            `Sponsor "${job.title}"?\n\n` +
            `This will:\n` +
            `• Feature your job at the top of the job board\n` +
            `• Add a "Sponsored" badge to your job\n` +
            `• Cost 1 token (you have ${tokens} tokens)\n\n` +
            `The sponsorship will last for 7 days.`
        )) {
            return;
        }

        setSponsoring(prev => ({ ...prev, [job.id]: true }));

        try {
            const deductResult = await deductTokens(
                recruiterId,
                1,
                `Sponsored job: ${job.title}`
            );

            if (!deductResult.success) {
                alert('Failed to sponsor job: ' + deductResult.error);
                setSponsoring(prev => ({ ...prev, [job.id]: false }));
                return;
            }

            const jobRef = doc(db, 'jobs', job.id);
            await updateDoc(jobRef, {
                isSponsored: true,
                sponsoredUntil: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
                updatedAt: new Date().toISOString()
            });

            alert(`✅ "${job.title}" is now sponsored for 7 days!`);
            onRefresh();
        } catch (error) {
            console.error("Error sponsoring job:", error);
            alert('Failed to sponsor job. Please try again.');
        } finally {
            setSponsoring(prev => ({ ...prev, [job.id]: false }));
        }
    };

    const handleEdit = (job) => {
        setEditingJob(job);
        setShowEditModal(true);
    };

    const handleEditSave = async (updatedData) => {
        try {
            const jobRef = doc(db, 'jobs', editingJob.id);
            
            // Don't allow changing job type or postedBy
            const updateData = {
                ...updatedData,
                updatedAt: new Date().toISOString()
            };
            
            await updateDoc(jobRef, updateData);
            setShowEditModal(false);
            setEditingJob(null);
            onRefresh();
            alert('Job updated successfully!');
            return { success: true };
        } catch (error) {
            console.error("Error updating job:", error);
            alert('Failed to update job. Please try again.');
            return { success: false, error: error.message };
        }
    };

    const handleDelete = async (job) => {
        if (!window.confirm(
            `Are you sure you want to delete "${job.title}"?\n\n` +
            `This action cannot be undone.`
        )) {
            return;
        }

        setDeleting(prev => ({ ...prev, [job.id]: true }));

        try {
            await deleteDoc(doc(db, 'jobs', job.id));
            alert('Job deleted successfully!');
            onRefresh();
        } catch (error) {
            console.error("Error deleting job:", error);
            alert('Failed to delete job. Please try again.');
        } finally {
            setDeleting(prev => ({ ...prev, [job.id]: false }));
        }
    };

    return (
        <>
            <div className="bg-white rounded-xl shadow-md overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
                    <h3 className="font-bold text-gradpath-dark">My Job Postings</h3>
                    <span className="text-sm text-gray-500">{jobs.length} jobs</span>
                </div>
                
                {jobs.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                        <i className="fas fa-briefcase text-4xl text-gray-300 mb-3"></i>
                        <p>No jobs posted yet</p>
                        <button 
                            onClick={() => document.querySelector('[data-tab="post-job"]')?.click()} 
                            className="text-gradpath-green hover:underline text-sm"
                        >
                            Post your first job
                        </button>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Title</th>
                                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Location</th>
                                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Applications</th>
                                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Status</th>
                                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Sponsored</th>
                                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Closing</th>
                                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {jobs.map(job => (
                                    <tr key={job.id} className="border-t border-gray-200 hover:bg-gray-50">
                                        <td className="px-4 py-3 text-sm font-medium">{job.title}</td>
                                        <td className="px-4 py-3 text-sm">{job.city || job.province}</td>
                                        <td className="px-4 py-3 text-sm">{job.applications?.length || 0}</td>
                                        <td className="px-4 py-3 text-sm">
                                            <span className={`px-2 py-1 rounded-full text-xs ${getStatusColor(job)}`}>
                                                {new Date(job.closingDate) < new Date() ? 'Closed' : 'Active'}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3 text-sm">
                                            {job.isSponsored ? (
                                                <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded-full">
                                                    <i className="fas fa-star mr-1"></i> Sponsored
                                                </span>
                                            ) : (
                                                <span className="text-xs text-gray-400">Not sponsored</span>
                                            )}
                                        </td>
                                        <td className="px-4 py-3 text-sm">{formatDate(job.closingDate)}</td>
                                        <td className="px-4 py-3 text-sm">
                                            <div className="flex flex-wrap gap-1">
                                                {/* Edit Button */}
                                                <button
                                                    onClick={() => handleEdit(job)}
                                                    className="px-2 py-1 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-xs transition flex items-center gap-1"
                                                >
                                                    <i className="fas fa-edit"></i> Edit
                                                </button>

                                                {/* Delete Button */}
                                                <button
                                                    onClick={() => handleDelete(job)}
                                                    disabled={deleting[job.id]}
                                                    className="px-2 py-1 bg-red-600 text-white rounded-lg hover:bg-red-700 text-xs transition flex items-center gap-1 disabled:opacity-50"
                                                >
                                                    {deleting[job.id] ? (
                                                        <i className="fas fa-spinner fa-spin"></i>
                                                    ) : (
                                                        <i className="fas fa-trash"></i>
                                                    )}
                                                    {deleting[job.id] ? '...' : 'Delete'}
                                                </button>

                                                {/* Sponsor Button */}
                                                {!job.isSponsored && new Date(job.closingDate) >= new Date() && (
                                                    <button
                                                        onClick={() => handleSponsor(job)}
                                                        disabled={sponsoring[job.id]}
                                                        className="px-2 py-1 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 text-xs transition flex items-center gap-1 disabled:opacity-50"
                                                    >
                                                        {sponsoring[job.id] ? (
                                                            <i className="fas fa-spinner fa-spin"></i>
                                                        ) : (
                                                            <i className="fas fa-star"></i>
                                                        )}
                                                        {sponsoring[job.id] ? '...' : 'Sponsor'}
                                                    </button>
                                                )}
                                                {job.isSponsored && (
                                                    <span className="text-xs text-green-600 flex items-center gap-1">
                                                        <i className="fas fa-check-circle"></i> Active
                                                    </span>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Edit Job Modal */}
            {showEditModal && editingJob && (
                <JobFormModal
                    isOpen={showEditModal}
                    onClose={() => {
                        setShowEditModal(false);
                        setEditingJob(null);
                    }}
                    onSave={handleEditSave}
                    initialData={editingJob}
                    provinces={['Eastern Cape', 'Free State', 'Gauteng', 'KwaZulu-Natal', 'Limpopo', 'Mpumalanga', 'Northern Cape', 'North West', 'Western Cape']}
                />
            )}
        </>
    );
};

export default JobPostings;